import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useFetch } from "./useFetch";
import { useApiCall } from "./useApiCall";
import { clubsApi, membersApi, membershipsApi, attendancesApi } from "@/api";
import { useNotification } from "@/context/NotificationContext";
import type { Club, Member, Membership, Attendance } from "@/types";
import { HOME_RECENT_ATTENDANCES_LIMIT } from "@/constants";
import { useApiErrorNotify } from "./useApiErrorNotify";
import { useFormatShortDateTime } from "./useFormatShortDateTime";

export function useHomePage() {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const onApiError = useApiErrorNotify();
  const formatDateTime = useFormatShortDateTime();

  const {
    data: clubs,
    loading: clubsLoading,
    error: clubsError,
  } = useFetch<Club[]>(clubsApi.getClubs);
  const {
    data: members,
    loading: membersLoading,
    error: membersError,
  } = useFetch<Member[]>(membersApi.getMembers);
  const {
    data: memberships,
    loading: membershipsLoading,
    error: membershipsError,
  } = useFetch<Membership[]>(membershipsApi.getMemberships);
  const {
    data: attendances,
    loading: attendancesLoading,
    error: attendancesError,
    refresh: refreshAttendances,
  } = useFetch<Attendance[]>(attendancesApi.getAttendances);

  const loading =
    clubsLoading || membersLoading || membershipsLoading || attendancesLoading;
  const error =
    clubsError || membersError || membershipsError || attendancesError;

  const [checkInOpen, setCheckInOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState("");

  const { execute: doCreate, loading: creating } = useApiCall(
    attendancesApi.createAttendance,
    { onError: onApiError },
  );
  const { execute: doUpdate, loading: checkingOut } = useApiCall(
    attendancesApi.updateAttendance,
    { onError: onApiError },
  );

  const handleQuickCheckIn = async () => {
    if (!selectedMember) return;
    const ok = await doCreate({
      memberId: selectedMember,
      startedAt: new Date().toISOString(),
    });
    if (ok !== undefined) {
      notify(t("common.success"), "success");
      setCheckInOpen(false);
      setSelectedMember("");
      refreshAttendances();
    }
  };

  const handleQuickCheckOut = async (id: string) => {
    const ok = await doUpdate(id, { endedAt: new Date().toISOString() });
    if (ok !== undefined) {
      notify(t("common.success"), "success");
      refreshAttendances();
    }
  };

  const memberMap = useMemo(() => {
    const map = new Map<string, string>();
    (members ?? []).forEach((m) => map.set(m.id, m.name));
    return map;
  }, [members]);

  const recentAttendances = useMemo(() => {
    if (!Array.isArray(attendances)) return [];
    return [...attendances]
      .sort(
        (a, b) =>
          new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
      )
      .slice(0, HOME_RECENT_ATTENDANCES_LIMIT);
  }, [attendances]);

  const closeCheckInDialog = useCallback(() => setCheckInOpen(false), []);

  return {
    t,
    clubs,
    members,
    memberships,
    attendances,
    loading,
    error,
    checkInOpen,
    setCheckInOpen,
    selectedMember,
    setSelectedMember,
    creating,
    checkingOut,
    handleQuickCheckIn,
    handleQuickCheckOut,
    memberMap,
    recentAttendances,
    formatDateTime,
    closeCheckInDialog,
  };
}
